import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { getApiErrorMessage } from '../api';
import { useAuth } from '../hooks/useAuth';
import ModalShell from '../components/ModalShell';
import ClientProfileModal from '../components/ClientProfileModal';
import RecruiterProfileModal from '../components/RecruiterProfileModal';
import { ActionMenu, ActionMenuItem } from '../components/ActionMenu';
import { normalizeFieldText } from '../utils/textCase';
import SearchableClientSelect from '../components/SearchableClientSelect';
import SearchableRecruiterSelect from '../components/SearchableRecruiterSelect';
import MultiSelectFilter from '../components/MultiSelectFilter';
import QuickFilterGrid from '../components/QuickFilterGrid';
import ListFilterBar from '../components/ListFilterBar';
import FilterSearchField from '../components/FilterSearchField';
import { matchesOneOf, matchesRecruiterKeys } from '../utils/listFilters';
import SkillsPicker from '../components/SkillsPicker';
import JobMatchesModal from '../components/JobMatchesModal';
import {
  EDUCATIONAL_QUALIFICATION_OPTIONS,
  normalizeEducationalQualifications
} from '../constants/educationOptions';
import QualificationMultiSelect from '../components/QualificationMultiSelect';
import JobOpeningDetailBody from '../components/JobOpeningDetailBody';
import TableExportButton from '../components/TableExportButton';
import { jobColumns, mapJobs } from '../utils/dashboardColumns';
import SortableTh from '../components/SortableTh';
import { useSortableTable } from '../utils/sortableTable';
import { JOB_SORT_COLUMNS } from '../constants/listTableSort';
import {
  buildPosterCaption,
  renderJobPosterImage
} from '../utils/jobPoster';
import { EMPLOYMENT_TYPES } from '../constants/offer';
import {
  buildInterviewRoundsFromCount,
  defaultJobInterviewRounds,
  normalizeJobRoundsForm
} from '../constants/interviewRounds';
import { WORKING_MODES } from '../utils/candidateFormValidation';
import { budgetHintInr } from '../utils/amountInWordsInr';
import InterviewRoundsConfigModal from '../components/InterviewRoundsConfigModal';
import JobWizardStepBar, { validateJobWizardStep } from '../components/JobFormWizard';
import CandidateSectionHeader from '../components/CandidateSectionHeader';
import { candidateSectionClass } from '../constants/candidateFormStyles';
import {
  PORTAL_LIST_PAGE,
  PORTAL_LIST_TABLE_SCROLL,
  PORTAL_TABLE_CARD_STICKY
} from '../constants/portalUi';

/** Admin/recruiter: view summary + hiring poster. */
function canUseStaffJobTools(user) {
  return user?.role === 'admin' || user?.role === 'recruiter';
}

const JOB_STATUSES = ['open', 'in-progress', 'on-hold', 'closed', 'inactive'];
const JOB_TABLE_STATUSES = ['open', 'in-progress', 'on-hold', 'closed'];
const JOB_PRIORITIES = ['high', 'medium', 'low'];
const PREFERRED_GENDER_OPTIONS = ['Any', 'Male', 'Female', 'Other'];
const AGE_GROUP_OPTIONS = ['18-25', '26-35', '36-45', '46-55', '56+'];

function normalizeJobAgeGroups(job) {
  if (Array.isArray(job?.ageGroups) && job.ageGroups.length) {
    return job.ageGroups.filter(Boolean);
  }
  const legacy = String(job?.ageGroup || '').trim();
  if (!legacy) return [];
  return legacy.includes(';') ? legacy.split(';').map(s => s.trim()).filter(Boolean) : [legacy];
}

function normalizeSkills(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return String(value).split(',').map(s => s.trim()).filter(Boolean);
}

function buildJobFormFromRecord(job, { asCopy = false } = {}) {
  const baseTitle = (job.title || '').trim();
  return {
    title: asCopy && baseTitle ? `${baseTitle} (Copy)` : baseTitle,
    description: job.description || '',
    mustHaveSkills: [...(job.mustHaveSkills || [])],
    niceToHaveSkills: [...(job.niceToHaveSkills || [])],
    mustHaveTechnicalSkills: [...(job.mustHaveTechnicalSkills || [])],
    niceToHaveTechnicalSkills: [...(job.niceToHaveTechnicalSkills || [])],
    location: job.location || '',
    budgetFrom: job.budgetFrom || job.budget || '',
    budgetTo: job.budgetTo || '',
    experienceFrom: job.experienceFrom || (job.experienceBand ? job.experienceBand.split('-')[0] : '') || '',
    experienceTo: job.experienceTo || (job.experienceBand ? job.experienceBand.split('-')[1] : '') || '',
    employmentType: job.employmentType || '',
    noticePeriod: job.noticePeriod || '',
    weeklyWorkingDays: job.weeklyWorkingDays || '',
    preferredGender: job.preferredGender || 'Any',
    ageGroups: normalizeJobAgeGroups(job),
    educationalQualifications: normalizeEducationalQualifications(job),
    benefits: [...normalizeSkills(job.benefits || [])],
    numberOfPositions: job.numberOfPositions || 1,
    recruiterId: job.recruiterId || '',
    clientId: job.clientId || '',
    priority: job.priority || 'medium',
    status: asCopy ? 'open' : job.status || 'open',
    hiringNote: asCopy ? '' : job.hiringNote || '',
    numberOfInterviewRounds: Math.max(1, job.interviewRounds?.length || 1),
    interviewRounds: normalizeJobRoundsForm(
      job.interviewRounds?.length ? job.interviewRounds : defaultJobInterviewRounds()
    ),
    preferredWorkingModes: [...(job.preferredWorkingModes || [])]
  };
}

function buildJobSavePayload(form) {
  const educationalQualifications = form.educationalQualifications || [];
  return {
    title: form.title,
    description: form.description,
    location: form.location,
    budgetFrom: form.budgetFrom,
    budgetTo: form.budgetTo,
    experienceFrom: form.experienceFrom,
    experienceTo: form.experienceTo,
    employmentType: form.employmentType || '',
    noticePeriod: form.noticePeriod,
    weeklyWorkingDays: form.weeklyWorkingDays,
    preferredGender: form.preferredGender || 'Any',
    ageGroups: form.ageGroups || [],
    preferredWorkingModes: form.preferredWorkingModes || [],
    educationalQualifications,
    educationalQualification: educationalQualifications.join('; '),
    mustHaveSkills: form.mustHaveSkills,
    niceToHaveSkills: form.niceToHaveSkills,
    mustHaveTechnicalSkills: form.mustHaveTechnicalSkills || [],
    niceToHaveTechnicalSkills: form.niceToHaveTechnicalSkills || [],
    benefits: form.benefits,
    numberOfPositions: form.numberOfPositions,
    recruiterId: form.recruiterId,
    clientId: form.clientId,
    priority: form.priority,
    interviewRounds: normalizeJobRoundsForm(
      buildInterviewRoundsFromCount(form.numberOfInterviewRounds, form.interviewRounds)
    )
  };
}

function jobStatusClass(status) {
  const map = {
    open: 'bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))]',
    'in-progress': 'bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))]',
    'on-hold': 'bg-[hsl(var(--ds-warning)/0.15)] text-[hsl(var(--ds-warning-fg))]',
    closed: 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]',
    inactive: 'bg-[hsl(var(--ds-danger)/0.12)] text-[hsl(var(--ds-danger))]'
  };
  return map[status] || 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]';
}

export default function Jobs({
  embedded = false,
  embeddedEditId = null,
  initialJob = null,
  onEmbeddedClose = null
}) {
  const embeddedInitRef = useRef(false);
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ 
    queryKey: ['jobs'], 
    queryFn: () => api.get('/jobs').then(r => r.data),
    retry: 1,
    staleTime: 30_000,
    onError: (err) => {
      console.error('Error fetching jobs:', err);
    }
  });
  const { data: recruiters, isLoading: recruitersLoading } = useQuery({ 
    queryKey: ['recruiters'], 
    queryFn: async () => {
      try {
        if (user?.role === 'admin' || user?.role === 'recruiter') {
          const response = await api.get('/recruiters');
          return response.data || [];
        }
        return [];
      } catch (error) {
        console.error('Error fetching recruiters:', error);
        return [];
      }
    },
    enabled: !!user && (user.role === 'admin' || user.role === 'recruiter')
  });
  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get('/clients').then(r => r.data),
    enabled: !!user && (user.role === 'admin' || user.role === 'recruiter')
  });
  const { data: skillsList } = useQuery({
    queryKey: ['skills'],
    queryFn: () => api.get('/skills').then(r => r.data),
    enabled: !!user && (user.role === 'admin' || user.role === 'recruiter')
  });
  const { data: posterBranding } = useQuery({
    queryKey: ['poster-branding'],
    queryFn: () => api.get('/settings/poster-branding').then(r => r.data),
    enabled: !!user
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [clientFilters, setClientFilters] = useState([]);
  const [locationFilters, setLocationFilters] = useState([]);
  const [recruiterFilters, setRecruiterFilters] = useState([]);
  const [statusFilters, setStatusFilters] = useState([]);
  const [priorityFilters, setPriorityFilters] = useState([]);
  const [showInactiveView, setShowInactiveView] = useState(false);
  const [staffingFilters, setStaffingFilters] = useState([]);
  const [matches, setMatches] = useState({});
  const [matchMeta, setMatchMeta] = useState({});
  const [showMatchesModal, setShowMatchesModal] = useState(null);

  const filterOptions = useMemo(() => {
    const jobs = data || [];
    const clientMap = new Map();
    const locationSet = new Set();
    const recruiterMap = new Map();

    for (const job of jobs) {
      const clientKey = job.clientId || job.clientName || '';
      const clientLabel = job.clientName || job.clientId || '';
      if (clientKey) clientMap.set(clientKey, clientLabel);

      if (job.location?.trim()) locationSet.add(job.location.trim());

      const recKey = job.recruiterId || job.recruiterName || '';
      const recLabel = job.recruiterName || job.recruiterId || '';
      if (recKey) recruiterMap.set(recKey, recLabel);
    }

    return {
      clients: [...clientMap.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      locations: [...locationSet].sort((a, b) => a.localeCompare(b)),
      recruiters: [...recruiterMap.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label))
    };
  }, [data]);

  const recruiterFilterOptions = useMemo(
    () => [
      { value: '__unassigned__', label: 'Unassigned' },
      ...filterOptions.recruiters.filter(r => r.value)
    ],
    [filterOptions.recruiters]
  );

  const inactiveJobs = useMemo(
    () => (data || []).filter(job => (job.status || '').toLowerCase() === 'inactive'),
    [data]
  );

  const activeJobs = useMemo(
    () => (data || []).filter(job => (job.status || '').toLowerCase() !== 'inactive'),
    [data]
  );

  const statusFilterOptions = useMemo(
    () => JOB_TABLE_STATUSES.map(s => ({ value: s, label: s.replace('-', ' ') })),
    []
  );

  const priorityFilterOptions = useMemo(
    () => JOB_PRIORITIES.map(p => ({ value: p, label: p })),
    []
  );

  const staffingFilterOptions = useMemo(
    () => [
      { value: 'open', label: 'Open headcount' },
      { value: 'filled', label: 'Fully filled' },
      { value: 'multi', label: 'Multi-position (2+)' }
    ],
    []
  );

  const tableJobs = useMemo(
    () => (showInactiveView ? inactiveJobs : activeJobs),
    [showInactiveView, inactiveJobs, activeJobs]
  );

  const filteredJobs = useMemo(() => {
    if (!tableJobs.length) return [];
    const q = searchQuery.trim().toLowerCase();
    return tableJobs.filter(job => {
      const status = (job.status || 'open').toLowerCase();
      const clientKey = job.clientId || job.clientName || '';
      const recKey = job.recruiterId || job.recruiterName || '';
      const balance =
        job.balanceToRecruit ??
        Math.max(0, (job.numberOfPositions || 1) - (job.joinedCount || 0));
      const positions = job.numberOfPositions || 1;

      if (!matchesOneOf(clientFilters, clientKey)) return false;
      if (!matchesOneOf(locationFilters, (job.location || '').trim())) return false;
      if (!matchesRecruiterKeys(recruiterFilters, recKey)) return false;
      if (!matchesOneOf(statusFilters, status)) return false;
      if (!matchesOneOf(priorityFilters, job.priority || 'medium')) return false;

      if (staffingFilters.length) {
        const staffingOk = staffingFilters.some(f => {
          if (f === 'open') return balance > 0;
          if (f === 'filled') return balance <= 0;
          if (f === 'multi') return positions > 1;
          return false;
        });
        if (!staffingOk) return false;
      }

      if (!q) return true;
      const haystack = [
        job.id,
        job.title,
        job.clientName,
        job.clientId,
        job.location,
        job.recruiterName,
        job.recruiterId,
        job.status,
        job.priority,
        job.hiringNote,
        job.noticePeriod,
        job.weeklyWorkingDays,
        ...(normalizeEducationalQualifications(job)),
        job.educationalQualification,
        ...(job.mustHaveSkills || []),
        ...(job.niceToHaveSkills || []),
        ...(job.benefits || [])
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [
    tableJobs,
    searchQuery,
    clientFilters,
    locationFilters,
    recruiterFilters,
    statusFilters,
    priorityFilters,
    staffingFilters
  ]);

  const { sort, toggleSort, sortedRows: sortedJobs } = useSortableTable(
    filteredJobs,
    JOB_SORT_COLUMNS
  );

  const hasActiveFilters = Boolean(
    searchQuery.trim() ||
    clientFilters.length > 0 ||
    locationFilters.length > 0 ||
    recruiterFilters.length > 0 ||
    statusFilters.length > 0 ||
    priorityFilters.length > 0 ||
    staffingFilters.length > 0
  );

  const clearFilters = () => {
    setSearchQuery('');
    setClientFilters([]);
    setLocationFilters([]);
    setRecruiterFilters([]);
    setStatusFilters([]);
    setPriorityFilters([]);
    setStaffingFilters([]);
  };

  const [showSkillsModal, setShowSkillsModal] = useState(null);
  const [showRecruiterModal, setShowRecruiterModal] = useState(null);
  const [profileClientId, setProfileClientId] = useState(null);
  const [profileRecruiterId, setProfileRecruiterId] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    mustHaveSkills: [],
    niceToHaveSkills: [],
    mustHaveTechnicalSkills: [],
    niceToHaveTechnicalSkills: [],
    location: '',
    budgetFrom: '',
    budgetTo: '',
    experienceFrom: '',
    experienceTo: '',
    employmentType: '',
    noticePeriod: '',
    weeklyWorkingDays: '',
    preferredGender: 'Any',
    ageGroups: [],
    educationalQualifications: [],
    benefits: [],
    numberOfPositions: 1,
    recruiterId: '',
    clientId: '',
    priority: 'medium',
    status: 'open',
    hiringNote: '',
    numberOfInterviewRounds: 1,
    interviewRounds: defaultJobInterviewRounds(),
    preferredWorkingModes: []
  });
  useEffect(() => {
    if (!window.__PACE_HIRING__ || form.clientId) return;
    const firstClientId = Array.isArray(clients) ? clients[0]?.id : null;
    const internalCompanyId = user?.companyId || user?.company_id;
    const internalClientId =
      firstClientId || (internalCompanyId ? `ORG-${internalCompanyId}` : '');
    if (internalClientId) {
      setForm(current => ({ ...current, clientId: internalClientId }));
    }
  }, [clients, form.clientId, user?.companyId, user?.company_id]);
  const [showModal, setShowModal] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const closeJobModal = () => {
    setShowModal(false);
    setWizardStep(1);
    setEditing(null);
    if (embedded && onEmbeddedClose) onEmbeddedClose();
  };
  const [roundsConfigOpen, setRoundsConfigOpen] = useState(false);
  const [roundsViewOpen, setRoundsViewOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdTarget, setHoldTarget] = useState(null);
  const [holdForm, setHoldForm] = useState({ scope: 'all', positionsOnHold: 1 });
  const [showPosterModal, setShowPosterModal] = useState(false);
  const [posterImageUrl, setPosterImageUrl] = useState('');
  const [posterCaption, setPosterCaption] = useState('');
  const [posterSourceJob, setPosterSourceJob] = useState(null);
  const [viewJobSummary, setViewJobSummary] = useState(null);
  const [isGeneratingPoster, setIsGeneratingPoster] = useState(false);

  const staffJobTools = canUseStaffJobTools(user);

  const staffCanEditSkills = user?.role === 'admin' || user?.role === 'recruiter';

  const toggleJobWorkingMode = mode => {
    setForm(f => {
      const cur = f.preferredWorkingModes || [];
      const next = cur.includes(mode) ? cur.filter(m => m !== mode) : [...cur, mode];
      return { ...f, preferredWorkingModes: next };
    });
  };

  const benefitsCatalog = useMemo(() => {
    const bag = new Set();
    for (const job of data || []) {
      for (const benefit of normalizeSkills(job?.benefits)) {
        if (benefit) bag.add(benefit);
      }
    }
    for (const benefit of normalizeSkills(form?.benefits)) {
      if (benefit) bag.add(benefit);
    }
    return [...bag].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [data, form?.benefits]);

  const createJob = useMutation({
    mutationFn: payload => {
      const body = {
        ...payload,
        mustHaveSkills: normalizeSkills(payload.mustHaveSkills),
        niceToHaveSkills: normalizeSkills(payload.niceToHaveSkills),
        mustHaveTechnicalSkills: normalizeSkills(payload.mustHaveTechnicalSkills),
        niceToHaveTechnicalSkills: normalizeSkills(payload.niceToHaveTechnicalSkills),
        benefits: normalizeSkills(payload.benefits)
      };
      return api.post('/jobs', body, { timeout: 120000 }).then(r => r.data);
    },
    onSuccess: (createdJob) => {
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      closeJobModal();
      setForm({
        title: '',
        description: '',
        mustHaveSkills: [],
        niceToHaveSkills: [],
        location: '',
        budgetFrom: '',
        budgetTo: '',
        experienceFrom: '',
        experienceTo: '',
        employmentType: '',
        noticePeriod: '',
        weeklyWorkingDays: '',
        preferredGender: 'Any',
        ageGroups: [],
        educationalQualifications: [],
        benefits: [],
        numberOfPositions: 1,
        recruiterId: '',
        clientId: '',
        priority: 'medium',
        status: 'open',
        hiringNote: '',
        numberOfInterviewRounds: 1,
        interviewRounds: defaultJobInterviewRounds(),
        preferredWorkingModes: []
      });
      if (staffJobTools && !embedded) openPosterGenerator(createdJob);
    },
    onError: err => {
      alert(err.response?.data?.message || err.message || 'Failed to create job');
    }
  });

  const loadMatches = async (jobId) => {
    try {
      const res = await api.get('/match', { params: { jobId } });
      const payload = res.data;
      const list = Array.isArray(payload) ? payload : payload.matches || [];
      setMatches(prev => ({ ...prev, [jobId]: list }));
      setMatchMeta(prev => ({
        ...prev,
        [jobId]: Array.isArray(payload)
          ? null
          : {
              ...payload,
              job: payload.job || (data || []).find(j => j.id === jobId)
            }
      }));
      setShowMatchesModal(jobId);
    } catch (error) {
      console.error('Error loading matches:', error);
      alert(error?.response?.data?.message || 'Failed to load matches');
    }
  };

  const addSkillMutation = useMutation({
    mutationFn: skill => api.post('/skills', { skill }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['skills'] })
  });

  const handleCreateSkill = async skill => {
    await addSkillMutation.mutateAsync(skill);
  };

  const submitCandidate = useMutation({
    mutationFn: ({ jobId, candidateId }) => api.post('/applications', { jobId, candidateId }).then(r => r.data),
    onSuccess: (_data, { jobId }) => {
      qc.invalidateQueries({ queryKey: ['applications'] });
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      loadMatches(jobId);
      alert('Candidate submitted to job');
    },
    onError: (err) => {
      alert(err.response?.data?.message || 'Failed to submit candidate');
    }
  });

  const updateJob = useMutation({
    mutationFn: (payload) => {
      const { id, ...rest } = payload;
      return api.put(`/jobs/${id}`, {
        ...rest,
        mustHaveSkills: normalizeSkills(rest.mustHaveSkills),
        niceToHaveSkills: normalizeSkills(rest.niceToHaveSkills),
        mustHaveTechnicalSkills: normalizeSkills(rest.mustHaveTechnicalSkills),
        niceToHaveTechnicalSkills: normalizeSkills(rest.niceToHaveTechnicalSkills),
        benefits: normalizeSkills(rest.benefits)
      }, { timeout: 120000 }).then(r => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['applications'] });
      closeJobModal();
      setEditing(null);
    },
    onError: err => {
      alert(err.response?.data?.message || err.message || 'Failed to save job changes');
    }
  });

  const deactivateJob = useMutation({
    mutationFn: (id) => api.put(`/jobs/${id}`, { status: 'inactive' }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    }
  });

  const reactivateJob = useMutation({
    mutationFn: (id) => api.put(`/jobs/${id}`, { status: 'reactivate' }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      setShowInactiveView(false);
    },
    onError: err => {
      alert(err.response?.data?.message || err.message || 'Failed to reactivate job');
    }
  });

  const deleteJob = useMutation({
    mutationFn: (id) => api.delete(`/jobs/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    }
  });

  const assignRecruiter = useMutation({
    mutationFn: ({ jobId, recruiterId }) => api.put(`/jobs/${jobId}`, { recruiterId }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['applications'] });
      setShowRecruiterModal(null);
    }
  });

  const formatToLakhs = (value) => {
    if (!value && value !== 0) return '';
    const num = Number(value);
    if (isNaN(num)) return value;
    const lakhs = num / 100000;
    // Round to 1 decimal if needed, otherwise show as integer
    if (lakhs % 1 === 0) {
      return `${lakhs}L`;
    } else {
      return `${lakhs.toFixed(1)}L`;
    }
  };

  const openModal = (job = null) => {
    setEditing(job);
    if (job) {
      setForm(buildJobFormFromRecord(job));
    } else {
      setForm({
        title: '',
        description: '',
        mustHaveSkills: [],
        niceToHaveSkills: [],
        location: '',
        budgetFrom: '',
        budgetTo: '',
        experienceFrom: '',
        experienceTo: '',
        employmentType: '',
        noticePeriod: '',
        weeklyWorkingDays: '',
        preferredGender: 'Any',
        ageGroups: [],
        educationalQualifications: [],
        benefits: [],
        numberOfPositions: 1,
        recruiterId: '',
        clientId: '',
        priority: 'medium',
        status: 'open',
        hiringNote: '',
        numberOfInterviewRounds: 1,
        interviewRounds: defaultJobInterviewRounds(),
        preferredWorkingModes: []
      });
    }
    setRoundsConfigOpen(false);
    setRoundsViewOpen(false);
    setWizardStep(1);
    setShowModal(true);
  };

  const openCloneModal = (job) => {
    setEditing(null);
    setForm(buildJobFormFromRecord(job, { asCopy: true }));
    setRoundsConfigOpen(false);
    setRoundsViewOpen(false);
    setWizardStep(1);
    setShowModal(true);
  };

  useEffect(() => {
    if (!embedded || !embeddedEditId || embeddedInitRef.current) return;
    const job =
      initialJob?.id === embeddedEditId
        ? initialJob
        : data?.find(j => j.id === embeddedEditId);
    if (job) {
      embeddedInitRef.current = true;
      openModal(job);
    }
  }, [embedded, embeddedEditId, initialJob, data]);

  const goWizardNext = () => {
    const err = validateJobWizardStep(wizardStep, form, user);
    if (err) {
      alert(err);
      return;
    }
    setWizardStep(s => Math.min(3, s + 1));
  };

  const goWizardBack = () => setWizardStep(s => Math.max(1, s - 1));

  const toAbsoluteAssetUrl = (pathValue) => {
    if (!pathValue) return '';
    if (/^https?:\/\//i.test(pathValue)) return pathValue;
    return `${window.location.origin}${pathValue.startsWith('/') ? '' : '/'}${pathValue}`;
  };

  const jobPayloadForPoster = (jobLike = {}) => ({
    title: jobLike.title || form.title,
    location: jobLike.location || form.location,
    description: jobLike.description || form.description,
    mustHaveSkills: jobLike.mustHaveSkills || form.mustHaveSkills,
    mustHaveTechnicalSkills: jobLike.mustHaveTechnicalSkills || form.mustHaveTechnicalSkills,
    niceToHaveSkills: jobLike.niceToHaveSkills || form.niceToHaveSkills,
    budgetFrom: jobLike.budgetFrom ?? form.budgetFrom,
    budgetTo: jobLike.budgetTo ?? form.budgetTo,
    experienceFrom: jobLike.experienceFrom ?? form.experienceFrom,
    experienceTo: jobLike.experienceTo ?? form.experienceTo,
    employmentType: jobLike.employmentType ?? form.employmentType,
    noticePeriod: jobLike.noticePeriod ?? form.noticePeriod,
    weeklyWorkingDays: jobLike.weeklyWorkingDays ?? form.weeklyWorkingDays,
    preferredGender: jobLike.preferredGender ?? form.preferredGender,
    ageGroups: normalizeJobAgeGroups(jobLike).length ? normalizeJobAgeGroups(jobLike) : form.ageGroups,
    educationalQualifications:
      normalizeEducationalQualifications(jobLike).length > 0
        ? normalizeEducationalQualifications(jobLike)
        : form.educationalQualifications,
    educationalQualification:
      normalizeEducationalQualifications(jobLike).join('; ') ||
      form.educationalQualifications?.join('; ') ||
      '',
    benefits: normalizeSkills(jobLike.benefits ?? form.benefits),
    numberOfPositions: jobLike.numberOfPositions ?? form.numberOfPositions
  });

  const openPosterGenerator = async (jobLike = {}) => {
    if (!(jobLike.title || form.title || '').trim()) {
      alert('Enter job title before generating poster.');
      return;
    }
    setIsGeneratingPoster(true);
    try {
      const job = jobPayloadForPoster(jobLike);
      const clientId = jobLike.clientId || form.clientId;
      const client = (clients || []).find(c => c.id === clientId);
      const branding = {
        appName: posterBranding?.appName || 'Paytiemp',
        tagline: posterBranding?.tagline || '',
        phone: posterBranding?.phone || '',
        email: posterBranding?.email || '',
        website: posterBranding?.website || '',
        natureOfBusiness: client?.natureOfBusiness || ''
      };
      const imageUrl = await renderJobPosterImage({
        job,
        branding,
        absoluteLogoUrl: toAbsoluteAssetUrl(posterBranding?.logoPath)
      });
      setPosterImageUrl(imageUrl);
      setPosterCaption(
        buildPosterCaption(job, {
          ...branding,
          natureOfBusiness: client?.natureOfBusiness || ''
        })
      );
      setPosterSourceJob(jobLike?.id ? jobLike : { ...jobLike, title: job.title });
      setShowPosterModal(true);
    } catch (err) {
      alert(err?.message || 'Failed to generate poster');
    } finally {
      setIsGeneratingPoster(false);
    }
  };

  const downloadPoster = () => {
    if (!posterImageUrl) return;
    const fileTitle = (posterSourceJob?.title || form.title || editing?.title || 'hiring-poster')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const link = document.createElement('a');
    link.href = posterImageUrl;
    link.download = `${fileTitle || 'hiring-poster'}-whatsapp-status.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const sharePosterOnWhatsApp = () => {
    (async () => {
      try {
        if (!posterImageUrl) {
          alert('Generate the poster first.');
          return;
        }

        // Prefer native share sheet (mobile) with the actual image file.
        const res = await fetch(posterImageUrl);
        const blob = await res.blob();
        const fileTitle = (posterSourceJob?.title || form.title || editing?.title || 'hiring-poster')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        const file = new File([blob], `${fileTitle || 'hiring-poster'}-whatsapp-status.png`, {
          type: 'image/png'
        });

        const canShareFiles =
          typeof navigator !== 'undefined' &&
          typeof navigator.share === 'function' &&
          (!navigator.canShare || navigator.canShare({ files: [file] }));

        if (canShareFiles) {
          await navigator.share({
            title: 'Hiring poster',
            text: posterCaption || '',
            files: [file]
          });
          return;
        }

        // Desktop fallback: download + open WhatsApp (text only) for instructions.
        downloadPoster();
        const text = encodeURIComponent(
          `${posterCaption}\n\nPoster downloaded.\nOpen WhatsApp → Status → Add Status → upload the downloaded image.`
        );
        window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
      } catch (err) {
        console.error('Share failed:', err);
        alert('Sharing is not supported on this device/browser. Please download and upload to WhatsApp Status manually.');
      }
    })();
  };

  const openHoldModal = (job) => {
    const balance = job.balanceToRecruit ?? Math.max(0, (job.numberOfPositions || 1) - (job.joinedCount || 0));
    if (balance < 1) {
      alert('No remaining positions to place on hold.');
      return;
    }
    setHoldTarget(job);
    setHoldForm({ scope: 'all', positionsOnHold: 1 });
    setShowHoldModal(true);
  };

  const confirmHold = () => {
    if (!holdTarget) return;
    const balance = holdTarget.balanceToRecruit ?? Math.max(0, (holdTarget.numberOfPositions || 1) - (holdTarget.joinedCount || 0));
    if (holdForm.scope === 'partial') {
      const n = Number(holdForm.positionsOnHold);
      if (!n || n < 1 || n > balance) {
        alert(`Enter positions to hold between 1 and ${balance}`);
        return;
      }
    }
    updateJob.mutate({
      id: holdTarget.id,
      status: 'on-hold',
      holdScope: holdForm.scope,
      positionsOnHold: holdForm.scope === 'partial' ? Number(holdForm.positionsOnHold) : undefined
    }, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['applications'] });
        setShowHoldModal(false);
        setHoldTarget(null);
      }
    });
  };

  const resumeHiring = (job) => {
    updateJob.mutate({ id: job.id, status: 'resume' });
  };

  const submitForm = () => {
    for (let step = 1; step <= 3; step += 1) {
      const err = validateJobWizardStep(step, form, user);
      if (err) {
        alert(err);
        setWizardStep(step);
        return;
      }
    }

    const paceClientId =
      (Array.isArray(clients) ? clients[0]?.id : '') ||
      (user?.companyId || user?.company_id
        ? `ORG-${user.companyId || user.company_id}`
        : '');
    const clientId =
      form.clientId ||
      editing?.clientId ||
      (window.__PACE_HIRING__ ? paceClientId : '');
    if (
      !window.__PACE_HIRING__ &&
      (user?.role === 'admin' || user?.role === 'recruiter') &&
      !clientId
    ) {
      alert('Please select a company for this job');
      setWizardStep(1);
      return;
    }

    const payload = buildJobSavePayload({ ...form, clientId });
    setRoundsConfigOpen(false);
    setRoundsViewOpen(false);

    if (editing?.id) {
      updateJob.mutate({ id: editing.id, ...payload });
    } else {
      createJob.mutate(payload);
    }
  };

  const saveError = createJob.error || updateJob.error;
  const saveErrorMessage = saveError?.response?.data?.message || saveError?.message;

  if (!embedded && isLoading) return <div className="p-6">Loading...</div>;
  
  if (!embedded && error) {
    return (
      <div className="p-6">
        <div className="text-red-600">{getApiErrorMessage(error, 'Error loading jobs. Please try again.')}</div>
        <button 
          className="mt-4 px-4 py-2 border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))]" 
          onClick={() => qc.invalidateQueries({ queryKey: ['jobs'] })}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!embedded && !user) return <div className="p-6">Please log in to view jobs.</div>;

  if (embedded && !showModal) {
    return (
      <div className="fixed inset-0 z-[14000] bg-black/50 flex items-center justify-center p-4">
        <div className="bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] rounded-xl px-6 py-4 text-sm text-[hsl(var(--ds-fg))] shadow-xl">
          Loading job…
        </div>
      </div>
    );
  }

  const staffListLayout = !embedded;

  return (
    <div className={embedded ? '' : staffListLayout ? `${PORTAL_LIST_PAGE} jobs-list-page` : 'space-y-4 min-w-0 max-w-full'}>
      {!embedded && (
      <>
      <div className="flex-shrink-0 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Jobs</h1>
        <div className="flex items-center gap-2">
          {inactiveJobs.length > 0 && (
            <button
              type="button"
              className={`px-3 py-2 text-sm font-medium rounded-xl border shadow-sm shrink-0 ${
                showInactiveView
                  ? 'border border-[hsl(var(--ds-danger))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-danger))] border-red-700 hover:bg-red-700'
                  : 'bg-red-50 text-red-800 border-red-200 hover:bg-red-100'
              }`}
              onClick={() => {
                setShowInactiveView(v => !v);
                if (!showInactiveView) setStatusFilters([]);
              }}
            >
              {showInactiveView ? 'Back to active jobs' : `Inactive (${inactiveJobs.length})`}
            </button>
          )}
          <TableExportButton
            filename={showInactiveView ? 'jobs-inactive' : 'jobs'}
            sheetName={showInactiveView ? 'Inactive Jobs' : 'Jobs'}
            columns={jobColumns}
            rows={mapJobs(sortedJobs)}
          />
          {user && (user.role === 'admin' || user.role === 'recruiter' || user.role === 'client') && (
            <button className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-3 py-2 text-sm font-medium" onClick={() => openModal()}>
              Add job
            </button>
          )}
        </div>
      </div>

      <div className="flex-shrink-0">
      <ListFilterBar>
        <QuickFilterGrid
          search={
            <FilterSearchField
              placeholder="Search by job, company, location, recruiter, skills..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery('')}
            />
          }
          filters={[
            <MultiSelectFilter
              key="client"
              options={filterOptions.clients}
              values={clientFilters}
              onChange={setClientFilters}
              emptyLabel="All companies"
            />,
            <MultiSelectFilter
              key="location"
              options={filterOptions.locations.map(loc => ({ value: loc, label: loc }))}
              values={locationFilters}
              onChange={setLocationFilters}
              emptyLabel="All locations"
            />,
            ...(user.role === 'admin'
              ? [
                  <MultiSelectFilter
                    key="recruiter"
                    options={recruiterFilterOptions}
                    values={recruiterFilters}
                    onChange={setRecruiterFilters}
                    emptyLabel="All recruiters"
                  />
                ]
              : []),
            <MultiSelectFilter
              key="staffing"
              options={staffingFilterOptions}
              values={staffingFilters}
              onChange={setStaffingFilters}
              emptyLabel="All positions"
            />,
            ...(!showInactiveView
              ? [
                  <MultiSelectFilter
                    key="status"
                    options={statusFilterOptions}
                    values={statusFilters}
                    onChange={setStatusFilters}
                    emptyLabel="All statuses"
                  />
                ]
              : []),
            <MultiSelectFilter
              key="priority"
              options={priorityFilterOptions}
              values={priorityFilters}
              onChange={setPriorityFilters}
              emptyLabel="All priorities"
            />
          ]}
        />
        {hasActiveFilters && (
          <div className="mt-2 flex items-center justify-between text-xs text-[hsl(var(--ds-muted-fg))]">
            <span>
              Showing {sortedJobs.length} of {tableJobs.length}{' '}
              {showInactiveView ? 'inactive' : 'active'} jobs
            </span>
            <button type="button" className="text-[hsl(var(--ds-primary))] hover:underline" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}
      </ListFilterBar>
      </div>
      </>
      )}

      {!embedded && (
        <div
          className={`${PORTAL_TABLE_CARD_STICKY} flex-1 min-h-0 flex flex-col min-w-0${
            showInactiveView ? ' border-red-200' : ''
          }`}
        >
        <div className={PORTAL_LIST_TABLE_SCROLL}>
          {(!data || activeJobs.length === 0) && inactiveJobs.length === 0 ? (
            <div className="px-3 py-8 text-center text-[hsl(var(--ds-muted-fg))]">No jobs found</div>
          ) : sortedJobs.length === 0 ? (
            <div className="px-3 py-8 text-center text-[hsl(var(--ds-muted-fg))]">
              {tableJobs.length === 0 && !hasActiveFilters
                ? showInactiveView
                  ? 'No inactive jobs'
                  : inactiveJobs.length > 0
                    ? 'No active jobs — use Inactive badge above to view deactivated jobs'
                    : 'No jobs found'
                : 'No jobs match your search or filters'}
            </div>
          ) : (
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col className="w-[6%]" />
              <col className="w-[22%]" />
              <col className="w-[16%]" />
              <col className="w-[10%]" />
              <col className="w-[10%]" />
              <col className="w-[10%]" />
              <col className="w-[10%]" />
              <col className="w-[10%]" />
              <col className="w-[6%]" />
            </colgroup>
            <thead className="bg-[hsl(var(--ds-muted))] border-b border-[hsl(var(--ds-border))] sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2">Sl No</th>
                <SortableTh label="Job" sortKey="id" sort={sort} onSort={toggleSort} className="px-3 py-2" />
                <SortableTh label="Company" sortKey="client" sort={sort} onSort={toggleSort} className="px-3 py-2" />
                <SortableTh label="Location" sortKey="location" sort={sort} onSort={toggleSort} className="px-3 py-2" />
                <SortableTh label="Budget" sortKey="budget" sort={sort} onSort={toggleSort} className="px-3 py-2" />
                <SortableTh label="Staffing" sortKey="staffing" sort={sort} onSort={toggleSort} className="px-3 py-2" />
                <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} className="px-3 py-2" />
                <SortableTh label="Recruiter" sortKey="recruiter" sort={sort} onSort={toggleSort} className="px-3 py-2" />
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
                {sortedJobs.map((job, idx) => (
                <tr key={job.id} className="border-b border-[hsl(var(--ds-border))]">
                  <td className="px-3 py-2 align-top text-xs text-[hsl(var(--ds-muted-fg))] whitespace-nowrap">{idx + 1}</td>
                  <td className="px-3 py-2 align-top">
                    <button
                      type="button"
                      className="font-semibold truncate text-left text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] hover:underline underline-offset-2 max-w-full block"
                      title={job.title}
                      onClick={() => openModal(job)}
                    >
                      {job.title}
                    </button>
                    <div className="text-xs text-[hsl(var(--ds-muted-fg))] truncate" title={job.id}>{job.id}</div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    {job.clientId ? (
                      <button
                        type="button"
                        className="truncate text-xs text-left text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] hover:underline underline-offset-2 max-w-full block"
                        title={job.clientName || job.clientId}
                        onClick={() => setProfileClientId(job.clientId)}
                      >
                        {job.clientName || job.clientId}
                      </button>
                    ) : (
                      <div className="truncate text-xs" title={job.clientName || job.clientId}>
                        {job.clientName || job.clientId}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="truncate text-xs" title={job.location || ''}>{job.location || '-'}</div>
                  </td>
                  <td className="px-3 py-2 align-top text-xs font-medium whitespace-nowrap">
                    {job.budgetFrom || job.budgetTo ? (
                      <>
                        {job.budgetFrom ? formatToLakhs(job.budgetFrom) : ''}
                        {job.budgetFrom && job.budgetTo ? '–' : ''}
                        {job.budgetTo ? formatToLakhs(job.budgetTo) : ''}
                      </>
                    ) : (
                      <span className="text-[hsl(var(--ds-muted-fg))]">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top text-xs whitespace-nowrap">
                    <div className="font-medium">{job.numberOfPositions || 1} pos</div>
                    {(job.numberOfPositions || 1) > 1 ? (
                      <div className={job.balanceToRecruit > 0 ? 'text-orange-600' : 'text-green-600'}>
                        {job.balanceToRecruit || 0} open
                      </div>
                    ) : job.joinedCount > 0 ? (
                      <div className="text-[hsl(var(--ds-muted-fg))]">{job.joinedCount} joined</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex flex-wrap gap-1">
                      <span className="px-1.5 py-0.5 text-[10px] bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] capitalize rounded">{job.priority}</span>
                      <span className={`px-1.5 py-0.5 text-[10px] capitalize rounded ${jobStatusClass(job.status)}`} title={job.hiringNote || ''}>{job.status?.replace('-', ' ')}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    {job.recruiterId ? (
                      <button
                        type="button"
                        className="text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] hover:underline text-xs truncate max-w-full block text-left"
                        title={job.recruiterName || job.recruiterId}
                        onClick={() => setProfileRecruiterId(job.recruiterId)}
                      >
                        {job.recruiterName || job.recruiterId}
                      </button>
                    ) : user?.role === 'admin' ? (
                      <button
                        type="button"
                        className="text-[hsl(var(--ds-primary))] hover:underline text-xs truncate max-w-full block text-left"
                        title="Assign recruiter"
                        onClick={() => setShowRecruiterModal(job)}
                      >
                        Assign
                      </button>
                    ) : (
                      <div className="text-xs truncate" title={job.recruiterName || ''}>{job.recruiterName || '-'}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top text-right">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <button
                        type="button"
                        className="text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] text-xs"
                        onClick={() => setShowSkillsModal(job)}
                        title="View skills"
                      >
                        Skills
                      </button>
                      {user && (user.role === 'admin' || user.role === 'recruiter') && (
                        <button
                          type="button"
                          className="text-[hsl(var(--ds-primary))] hover:underline text-xs"
                          onClick={() => loadMatches(job.id)}
                        >
                          Matches
                        </button>
                      )}
                      <ActionMenu>
                        {staffJobTools && (
                          <ActionMenuItem onClick={() => setViewJobSummary(job)}>View job</ActionMenuItem>
                        )}
                        <ActionMenuItem onClick={() => openModal(job)}>Edit</ActionMenuItem>
                        {showInactiveView ? (
                          user &&
                          (user.role === 'admin' || user.role === 'recruiter' || user.role === 'client') && (
                            <ActionMenuItem
                              onClick={() => {
                                if (window.confirm(`Reactivate "${job.title}"?`)) {
                                  reactivateJob.mutate(job.id);
                                }
                              }}
                            >
                              Reactivate
                            </ActionMenuItem>
                          )
                        ) : (
                          <>
                            {user && (user.role === 'admin' || user.role === 'recruiter' || user.role === 'client') && (
                              <ActionMenuItem onClick={() => openCloneModal(job)}>Copy job</ActionMenuItem>
                            )}
                            {staffJobTools && (
                              <ActionMenuItem onClick={() => openPosterGenerator(job)}>Generate hiring poster</ActionMenuItem>
                            )}
                            {job.status !== 'closed' && job.status !== 'inactive' && (
                              <ActionMenuItem onClick={() => openHoldModal(job)}>Put on hold</ActionMenuItem>
                            )}
                            {(job.status === 'on-hold' || job.holdScope) && job.status !== 'inactive' && (
                              <ActionMenuItem onClick={() => resumeHiring(job)}>Resume hiring</ActionMenuItem>
                            )}
                            <ActionMenuItem onClick={() => deactivateJob.mutate(job.id)}>Deactivate</ActionMenuItem>
                          </>
                        )}
                        <ActionMenuItem danger onClick={() => { if (window.confirm('Are you sure you want to delete this job?')) deleteJob.mutate(job.id); }}>Delete</ActionMenuItem>
                      </ActionMenu>
                    </div>
                  </td>
                </tr>
                ))}
            </tbody>
          </table>
          )}
        </div>
      </div>
      )}

      {showModal && (
        <ModalShell
          title={editing ? 'Edit job' : form.title?.includes('(Copy)') ? 'Copy job' : 'Add job'}
          subtitle={`Step ${wizardStep} of 3`}
          onClose={closeJobModal}
          maxWidth="max-w-3xl"
          zIndex={embedded ? 'z-[14000]' : undefined}
          curvy
          footer={
            <>
              {(createJob.isError || updateJob.isError) && (
                <span className="text-sm text-red-600 mr-auto">Error saving job{saveErrorMessage ? `: ${saveErrorMessage}` : ''}</span>
              )}
              {staffJobTools && wizardStep === 3 && (
                <button
                  type="button"
                className={`px-4 py-2 text-sm border border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-primary)/0.08)] disabled:opacity-60`}
                  onClick={() => openPosterGenerator(editing || form)}
                  disabled={isGeneratingPoster}
                >
                  {isGeneratingPoster ? 'Generating poster...' : 'Generate hiring poster'}
                </button>
              )}
              <button type="button" className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]" onClick={closeJobModal}>
                Cancel
              </button>
              {wizardStep > 1 && (
                <button type="button" className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]" onClick={goWizardBack}>
                  Back
                </button>
              )}
              {wizardStep < 3 ? (
                <button type="button" className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-4 py-2 text-sm font-medium" onClick={goWizardNext}>
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-4 py-2 text-sm font-medium disabled:opacity-50"
                  onClick={submitForm}
                  disabled={createJob.isLoading || updateJob.isLoading}
                >
                  {editing ? 'Save changes' : 'Add job'}
                </button>
              )}
            </>
          }
        >
          <JobWizardStepBar
            currentStep={wizardStep}
            onStepChange={setWizardStep}
            form={form}
            user={user}
          />
          <div className="grid gap-3 md:grid-cols-2">
            {wizardStep === 1 && (
              <>
              {(user.role === 'admin' || user.role === 'recruiter') && (
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))] block mb-1">Company</label>
                  <SearchableClientSelect
                    clients={clients || []}
                    value={form.clientId}
                    onChange={clientId => setForm(f => ({ ...f, clientId }))}
                  />
                </div>
              )}
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" placeholder=" " value={form.title} onChange={e => setForm(f => ({ ...f, title: normalizeFieldText(e.target.value, 'name') }))} onBlur={e => setForm(f => ({ ...f, title: normalizeFieldText(e.target.value, 'name', { final: true }) }))} />
                <label>Title</label>
              </div>
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" placeholder=" " value={form.location} onChange={e => setForm(f => ({ ...f, location: normalizeFieldText(e.target.value, 'name') }))} />
                <label>Location</label>
              </div>
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" type="number" min="1" placeholder=" " value={form.numberOfPositions} onChange={e => setForm(f => ({ ...f, numberOfPositions: parseInt(e.target.value) || 1 }))} />
                <label>Number of Positions</label>
              </div>
              <div className="field-with-label md:col-span-2">
                <textarea
                  className="w-full min-h-[5rem] rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))] resize-y whitespace-pre-wrap"
                  rows="4"
                  placeholder=" "
                  value={form.description}
                  onChange={e =>
                    setForm(f => ({ ...f, description: normalizeFieldText(e.target.value, 'multiline') }))
                  }
                  onBlur={e =>
                    setForm(f => ({
                      ...f,
                      description: normalizeFieldText(e.target.value, 'multiline', { final: true })
                    }))
                  }
                />
                <label>Description / JD</label>
              </div>
              </>
            )}

            {wizardStep === 2 && (
              <>
              <p className="md:col-span-2 ds-caption text-[hsl(var(--ds-muted-fg))] rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2">
                These fields drive <strong>View Matches</strong> scoring — same two categories as the candidate profile:{' '}
                <strong>Core Competencies</strong> and <strong>Technical Skills</strong>. Jobs use must-have vs
                nice-to-have within each category for ranking.
              </p>
              <div id="job-section-core-competencies" className={`md:col-span-2 ${candidateSectionClass(7)}`}>
                <CandidateSectionHeader
                  title="Core Competencies"
                  hint="Matched against candidate profile → Core Competencies (skills)"
                />
                <div className="space-y-3 mt-2">
                  <div>
                    <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))] block mb-1">Must-have</label>
                    <SkillsPicker
                      selectedSkills={Array.isArray(form.mustHaveSkills) ? form.mustHaveSkills : normalizeSkills(form.mustHaveSkills)}
                      onChange={mustHaveSkills => setForm(f => ({ ...f, mustHaveSkills }))}
                      catalog={skillsList || []}
                      onCreateSkill={handleCreateSkill}
                      label="Search core competencies"
                      placeholder="Search core competencies or type a new one…"
                      hint="Required competencies — weighted highest in match scoring."
                      allowRemoveSelected={staffCanEditSkills}
                      showCountBadge
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))] block mb-1">Nice-to-have</label>
                    <SkillsPicker
                      selectedSkills={Array.isArray(form.niceToHaveSkills) ? form.niceToHaveSkills : normalizeSkills(form.niceToHaveSkills)}
                      onChange={niceToHaveSkills => setForm(f => ({ ...f, niceToHaveSkills }))}
                      catalog={skillsList || []}
                      onCreateSkill={handleCreateSkill}
                      label="Search core competencies"
                      placeholder="Search core competencies or type a new one…"
                      hint="Optional competencies that add to the match score."
                      allowRemoveSelected={staffCanEditSkills}
                      showCountBadge
                    />
                  </div>
                </div>
              </div>
              <div id="job-section-technical-skills" className={`md:col-span-2 ${candidateSectionClass(8)}`}>
                <CandidateSectionHeader
                  title="Technical Skills"
                  hint="Matched against candidate profile → Technical Skills"
                />
                <div className="space-y-3 mt-2">
                  <div>
                    <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))] block mb-1">Must-have</label>
                    <SkillsPicker
                      selectedSkills={Array.isArray(form.mustHaveTechnicalSkills) ? form.mustHaveTechnicalSkills : normalizeSkills(form.mustHaveTechnicalSkills)}
                      onChange={mustHaveTechnicalSkills => setForm(f => ({ ...f, mustHaveTechnicalSkills }))}
                      catalog={skillsList || []}
                      onCreateSkill={handleCreateSkill}
                      label="Search technical skills"
                      placeholder="Search technical skills or type a new one…"
                      hint="Required tools, platforms, and technologies for match scoring."
                      allowRemoveSelected={staffCanEditSkills}
                      showCountBadge
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))] block mb-1">Nice-to-have</label>
                    <SkillsPicker
                      selectedSkills={Array.isArray(form.niceToHaveTechnicalSkills) ? form.niceToHaveTechnicalSkills : normalizeSkills(form.niceToHaveTechnicalSkills)}
                      onChange={niceToHaveTechnicalSkills => setForm(f => ({ ...f, niceToHaveTechnicalSkills }))}
                      catalog={skillsList || []}
                      onCreateSkill={handleCreateSkill}
                      label="Search technical skills"
                      placeholder="Search technical skills or type a new one…"
                      hint="Optional technical skills that add to the match score."
                      allowRemoveSelected={staffCanEditSkills}
                      showCountBadge
                    />
                  </div>
                </div>
              </div>
              <div className="min-w-0">
                <div className="field-with-label">
                  <input
                    className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                    type="text"
                    inputMode="numeric"
                    placeholder=" "
                    value={form.budgetFrom}
                    onChange={e =>
                      setForm(f => ({ ...f, budgetFrom: e.target.value.replace(/\D/g, '') }))
                    }
                  />
                  <label>Budget from (INR, per annum)</label>
                </div>
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1 break-words leading-snug">
                  {budgetHintInr(form.budgetFrom)}
                </p>
              </div>
              <div className="min-w-0">
                <div className="field-with-label">
                  <input
                    className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                    type="text"
                    inputMode="numeric"
                    placeholder=" "
                    value={form.budgetTo}
                    onChange={e =>
                      setForm(f => ({ ...f, budgetTo: e.target.value.replace(/\D/g, '') }))
                    }
                  />
                  <label>Budget to (INR, per annum)</label>
                </div>
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1 break-words leading-snug">
                  {budgetHintInr(form.budgetTo)}
                </p>
              </div>
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" type="number" placeholder=" " value={form.experienceFrom} onChange={e => setForm(f => ({ ...f, experienceFrom: e.target.value }))} />
                <label>Experience From (Years)</label>
              </div>
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" type="number" placeholder=" " value={form.experienceTo} onChange={e => setForm(f => ({ ...f, experienceTo: e.target.value }))} />
                <label>Experience To (Years)</label>
              </div>
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" placeholder=" " value={form.noticePeriod} onChange={e => setForm(f => ({ ...f, noticePeriod: normalizeFieldText(e.target.value, 'text') }))} />
                <label>Notice Period</label>
              </div>
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" placeholder=" " value={form.weeklyWorkingDays} onChange={e => setForm(f => ({ ...f, weeklyWorkingDays: normalizeFieldText(e.target.value, 'text') }))} />
                <label>Weekly Working Days</label>
              </div>
              <div className="field-with-label">
                <select className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" value={form.preferredGender || 'Any'} onChange={e => setForm(f => ({ ...f, preferredGender: e.target.value }))}>
                  {PREFERRED_GENDER_OPTIONS.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <label>Preferred Gender</label>
              </div>
              <QualificationMultiSelect
                options={AGE_GROUP_OPTIONS}
                value={form.ageGroups || []}
                onChange={ageGroups => setForm(f => ({ ...f, ageGroups }))}
                label="Preferred age group"
                placeholder="Select age groups"
                countLabelSingular="age group"
                countLabelPlural="age groups"
              />
              <QualificationMultiSelect
                options={EDUCATIONAL_QUALIFICATION_OPTIONS}
                value={form.educationalQualifications || []}
                onChange={educationalQualifications =>
                  setForm(f => ({ ...f, educationalQualifications }))
                }
                label="Educational Qualification"
                placeholder="Select qualifications"
                countLabelSingular="qualification"
                countLabelPlural="qualifications"
              />
              <div className="field-with-label">
                <select
                  className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                  value={form.employmentType || ''}
                  onChange={e => setForm(f => ({ ...f, employmentType: e.target.value }))}
                >
                  <option value="">Select employment type</option>
                  {EMPLOYMENT_TYPES.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <label>Employment type</label>
              </div>
              <div className="md:col-span-2 space-y-2">
                <div className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]">Preferred working mode</div>
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] -mt-1">
                  Used in candidate match scoring when the job specifies a mode (WFH, WFO, or Hybrid).
                </p>
                <div className="flex flex-wrap gap-2">
                  {WORKING_MODES.map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      className={`px-3 py-1 text-sm border ${
                        (form.preferredWorkingModes || []).includes(id)
                          ? 'border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)] text-[hsl(var(--ds-primary))]'
                          : 'bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]'
                      }`}
                      onClick={() => toggleJobWorkingMode(id)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              </>
            )}

            {wizardStep === 3 && (
              <>
              <div className="field-with-label">
                <select className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                <label>Priority</label>
              </div>
              <div className="field-with-label">
                <div className="min-h-[2.75rem] w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-3 text-sm text-[hsl(var(--ds-fg))] flex items-center pt-4 pb-2">
                  {editing ? (
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium capitalize ${jobStatusClass(form.status)}`}>
                      {(form.status || 'open').replace('-', ' ')}
                    </span>
                  ) : (
                    <span className="text-[hsl(var(--ds-muted-fg))] text-xs">Open (auto-managed)</span>
                  )}
                </div>
                <label>Status</label>
              </div>
              {editing && form.hiringNote && (
                <div className="md:col-span-2 ds-caption text-[hsl(var(--ds-muted-fg))] rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2">
                  {form.hiringNote}
                </div>
              )}
              <p className="md:col-span-2 text-[11px] text-[hsl(var(--ds-muted-fg))] -mt-1">
                Status updates automatically: Open → In progress when a recruiter is assigned or ATS activity starts →
                Closed when all positions are filled. Use <strong>Put on hold</strong> from the jobs list to pause hiring.
              </p>
              {user?.role === 'admin' && (
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))] block mb-1">Assign Recruiter</label>
                  <SearchableRecruiterSelect
                    recruiters={recruiters || []}
                    value={form.recruiterId}
                    onChange={recruiterId => setForm(f => ({ ...f, recruiterId }))}
                    allowEmpty
                    emptyLabel="Unassigned"
                    showHint={false}
                  />
                </div>
              )}
              <div className="md:col-span-2 border-t border-[hsl(var(--ds-border))] pt-3 mt-1">
                <p className="text-xs font-medium text-[hsl(var(--ds-fg))] mb-2">Interview process</p>
              </div>
              <div className="field-with-label md:col-span-2">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[8rem]">
                    <input
                      className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                      type="number"
                      min={1}
                      max={10}
                      value={form.numberOfInterviewRounds ?? 1}
                      onChange={e => {
                        const count = Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1));
                        setForm(f => ({
                          ...f,
                          numberOfInterviewRounds: count,
                          interviewRounds: buildInterviewRoundsFromCount(count, f.interviewRounds)
                        }));
                        if (count > 1) setRoundsConfigOpen(true);
                      }}
                    />
                    <label>Number of interview rounds</label>
                  </div>
                  {(form.numberOfInterviewRounds || 1) > 1 && (
                    <>
                      <button
                        type="button"
                        className="text-xs border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 hover:bg-[hsl(var(--ds-muted))] mb-0.5"
                        onClick={() => setRoundsConfigOpen(true)}
                      >
                        Configure rounds
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center w-9 h-9 border border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] mb-0.5"
                        title="View interview rounds"
                        aria-label="View interview rounds"
                        onClick={() => setRoundsViewOpen(true)}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">
                  Default is 1 round. Set 2 or more to name each stage (HR, technical, final, etc.).
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))] block mb-1">Benefits</label>
                <SkillsPicker
                  selectedSkills={Array.isArray(form.benefits) ? form.benefits : normalizeSkills(form.benefits)}
                  onChange={benefits => setForm(f => ({ ...f, benefits }))}
                  catalog={benefitsCatalog}
                  hint="Search existing benefits or type a new one to create it."
                  allowRemoveSelected={staffCanEditSkills}
                  showCountBadge
                />
              </div>
              </>
            )}
          </div>
        </ModalShell>
      )}

      <InterviewRoundsConfigModal
        open={roundsConfigOpen}
        roundCount={form.numberOfInterviewRounds || 1}
        rounds={form.interviewRounds}
        onChange={rounds => setForm(f => ({ ...f, interviewRounds: rounds }))}
        onClose={() => setRoundsConfigOpen(false)}
      />
      <InterviewRoundsConfigModal
        open={roundsViewOpen}
        roundCount={form.numberOfInterviewRounds || 1}
        rounds={form.interviewRounds}
        onChange={() => {}}
        onClose={() => setRoundsViewOpen(false)}
        readOnly
      />

      {showHoldModal && holdTarget && (
        <ModalShell
          title={`Put on hold — ${holdTarget.title}`}
          subtitle={`${holdTarget.balanceToRecruit ?? 0} position(s) still to fill (${holdTarget.joinedCount || 0} already joined)`}
          onClose={() => { setShowHoldModal(false); setHoldTarget(null); }}
          maxWidth="max-w-md"
          footer={
            <>
              <button type="button" className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]" onClick={() => { setShowHoldModal(false); setHoldTarget(null); }}>Cancel</button>
              <button type="button" className="px-4 py-2 text-sm font-medium border border-[hsl(var(--ds-warning))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-warning-fg))] hover:bg-amber-700 disabled:opacity-50" disabled={updateJob.isLoading} onClick={confirmHold}>
                Confirm hold
              </button>
            </>
          }
        >
          <div className="space-y-4 text-sm">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="holdScope"
                checked={holdForm.scope === 'all'}
                onChange={() => setHoldForm(f => ({ ...f, scope: 'all' }))}
                className="mt-1"
              />
              <span>
                <strong>All remaining positions</strong>
                <span className="block text-[hsl(var(--ds-muted-fg))] text-xs mt-0.5">Pauses hiring for every open slot ({holdTarget.balanceToRecruit ?? 0}). New offers are blocked until hiring resumes.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="holdScope"
                checked={holdForm.scope === 'partial'}
                onChange={() => setHoldForm(f => ({ ...f, scope: 'partial' }))}
                className="mt-1"
              />
              <span>
                <strong>Only some positions</strong>
                <span className="block text-[hsl(var(--ds-muted-fg))] text-xs mt-0.5">Hold part of the headcount; ATS continues for the rest.</span>
              </span>
            </label>
            {holdForm.scope === 'partial' && (
              <div className="field-with-label pl-6">
                <input
                  type="number"
                  min={1}
                  max={holdTarget.balanceToRecruit || 1}
                  className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                  placeholder=" "
                  value={holdForm.positionsOnHold}
                  onChange={e => setHoldForm(f => ({ ...f, positionsOnHold: parseInt(e.target.value, 10) || 1 }))}
                />
                <label>Positions to hold (max {holdTarget.balanceToRecruit ?? 1})</label>
              </div>
            )}
          </div>
        </ModalShell>
      )}

      {showMatchesModal && matches[showMatchesModal] && (
        <JobMatchesModal
          job={
            matchMeta[showMatchesModal]?.job || (data || []).find(j => j.id === showMatchesModal)
          }
          matches={matches[showMatchesModal]}
          matchMeta={matchMeta[showMatchesModal]}
          submitting={submitCandidate.isLoading}
          onClose={() => setShowMatchesModal(null)}
          onSubmit={candidateId => {
            submitCandidate.mutate({ jobId: showMatchesModal, candidateId });
          }}
        />
      )}

      {viewJobSummary && (
        <ModalShell
          title={viewJobSummary.title || 'Job summary'}
          subtitle={`Job ID: ${viewJobSummary.id || '—'} · ${viewJobSummary.status || 'open'}`}
          onClose={() => setViewJobSummary(null)}
          maxWidth="max-w-2xl"
          curvy
          footer={
            <>
              <button
                type="button"
                className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]"
                onClick={() => setViewJobSummary(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm border border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-primary)/0.08)] disabled:opacity-60"
                onClick={() => openPosterGenerator(viewJobSummary)}
                disabled={isGeneratingPoster}
              >
                {isGeneratingPoster ? 'Generating poster...' : 'Generate hiring poster'}
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))]"
                onClick={() => {
                  setViewJobSummary(null);
                  openModal(viewJobSummary);
                }}
              >
                Edit job
              </button>
            </>
          }
        >
          <JobOpeningDetailBody job={viewJobSummary} showStaffFields />
        </ModalShell>
      )}

      {staffJobTools && showPosterModal && (
        <ModalShell
          title="We are hiring"
          subtitle="WhatsApp / Instagram story size (1080 × 1920)"
          onClose={() => setShowPosterModal(false)}
          maxWidth="max-w-2xl"
          footer={
            <>
              <button
                type="button"
                className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]"
                onClick={() => setShowPosterModal(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm border border-green-300 text-green-700 bg-green-50 hover:bg-green-100"
                onClick={sharePosterOnWhatsApp}
              >
                Share on WhatsApp
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))]"
                onClick={downloadPoster}
              >
                Download poster
              </button>
            </>
          }
        >
          <div className="space-y-3">
            {posterImageUrl ? (
              <img src={posterImageUrl} alt="We are hiring poster preview" className="w-full max-h-[60vh] object-contain border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] rounded-lg" />
            ) : (
              <div className="text-sm text-[hsl(var(--ds-muted-fg))] border border-[hsl(var(--ds-border))] p-4">Poster preview is unavailable.</div>
            )}
            <div className="text-xs text-[hsl(var(--ds-muted-fg))] bg-[hsl(var(--ds-muted))] border border-[hsl(var(--ds-border))] p-3 rounded-lg">
              Light professional poster with QR code for candidate registration. Company logo, mobile, email, and website come from Settings → Company. Apply link defaults to hiring.paytiemp.com.
            </div>
          </div>
        </ModalShell>
      )}

      {/* View Skills Modal */}
      {showSkillsModal && (
        <ModalShell
          title="Job Skills"
          onClose={() => setShowSkillsModal(null)}
          maxWidth="max-w-md"
          footer={
            <button type="button" className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]" onClick={() => setShowSkillsModal(null)}>Close</button>
          }
        >
              <div className="space-y-4">
                <div>
                  <div className="font-semibold text-sm mb-2">Core Competencies</div>
                  <div className="text-xs text-[hsl(var(--ds-muted-fg))] mb-2">Must-have</div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(showSkillsModal.mustHaveSkills || []).length === 0 ? (
                      <span className="text-[hsl(var(--ds-muted-fg))] text-sm">None</span>
                    ) : (
                      (showSkillsModal.mustHaveSkills || []).map(s => (
                        <span key={`m-${s}`} className="px-2 py-1 text-xs bg-[hsl(var(--ds-primary)/0.06)] text-[hsl(var(--ds-primary))] border border-[hsl(var(--ds-primary)/0.2)]">{s}</span>
                      ))
                    )}
                  </div>
                  <div className="text-xs text-[hsl(var(--ds-muted-fg))] mb-2">Nice-to-have</div>
                  <div className="flex flex-wrap gap-2">
                    {(showSkillsModal.niceToHaveSkills || []).length === 0 ? (
                      <span className="text-[hsl(var(--ds-muted-fg))] text-sm">None</span>
                    ) : (
                      (showSkillsModal.niceToHaveSkills || []).map(s => (
                        <span key={`n-${s}`} className="px-2 py-1 text-xs bg-amber-50 text-amber-700 border border-amber-100">{s}</span>
                      ))
                    )}
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-sm mb-2">Technical Skills</div>
                  <div className="text-xs text-[hsl(var(--ds-muted-fg))] mb-2">Must-have</div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(showSkillsModal.mustHaveTechnicalSkills || []).length === 0 ? (
                      <span className="text-[hsl(var(--ds-muted-fg))] text-sm">None</span>
                    ) : (
                      (showSkillsModal.mustHaveTechnicalSkills || []).map(s => (
                        <span key={`tm-${s}`} className="px-2 py-1 text-xs bg-[hsl(var(--ds-primary)/0.06)] text-[hsl(var(--ds-primary))] border border-[hsl(var(--ds-primary)/0.2)]">{s}</span>
                      ))
                    )}
                  </div>
                  <div className="text-xs text-[hsl(var(--ds-muted-fg))] mb-2">Nice-to-have</div>
                  <div className="flex flex-wrap gap-2">
                    {(showSkillsModal.niceToHaveTechnicalSkills || []).length === 0 ? (
                      <span className="text-[hsl(var(--ds-muted-fg))] text-sm">None</span>
                    ) : (
                      (showSkillsModal.niceToHaveTechnicalSkills || []).map(s => (
                        <span key={`tn-${s}`} className="px-2 py-1 text-xs bg-[hsl(var(--ds-secondary)/0.08)] text-[hsl(var(--ds-secondary))] border border-[hsl(var(--ds-secondary)/0.2)]">{s}</span>
                      ))
                    )}
                  </div>
                </div>
              </div>
        </ModalShell>
      )}

      {/* Assign Recruiter Modal */}
      {showRecruiterModal && (
        <ModalShell
          title="Assign Recruiter"
          subtitle={`${showRecruiterModal.title} (${showRecruiterModal.id})`}
          onClose={() => setShowRecruiterModal(null)}
          maxWidth="max-w-md"
          footer={
            <button type="button" className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]" onClick={() => setShowRecruiterModal(null)}>Close</button>
          }
        >
              <div>
                <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))] block mb-1">Select Recruiter</label>
                <SearchableRecruiterSelect
                  recruiters={recruiters || []}
                  value={showRecruiterModal.recruiterId || ''}
                  onChange={recruiterId => {
                    assignRecruiter.mutate({
                      jobId: showRecruiterModal.id,
                      recruiterId: recruiterId || null
                    });
                    setShowRecruiterModal(null);
                  }}
                  allowEmpty
                  emptyLabel="Unassigned"
                  loading={recruitersLoading}
                  showHint={false}
                />
              </div>
        </ModalShell>
      )}

      {profileClientId && (
        <ClientProfileModal clientId={profileClientId} onClose={() => setProfileClientId(null)} />
      )}

      {profileRecruiterId && (
        <RecruiterProfileModal
          recruiterId={profileRecruiterId}
          onClose={() => setProfileRecruiterId(null)}
        />
      )}
    </div>
  );
}

